import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, Paper,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup
} from "@mui/material";
import moment from "moment";
import { useAsync2, useAsyncAction } from "nate-react-api-helpers";
import { useState } from "react";
import { api } from "../../api/API";
import { AdminUser } from "../../api/Admin";
import { RowMenu } from "../../misc/RowMenu";
import { useSnackAction } from "../../misc/SnackAction";
import { usePhone } from "../../misc/usePhone";

type Filter = "active" | "unconfirmed" | "archived" | "all";

export function AdminUsers() {
    const [filter, setFilter] = useState<Filter>("active");
    const [editing, setEditing] = useState<Partial<AdminUser> | null>(null);
    const phone = usePhone();

    const list = useAsync2(() => api.admin.listUsers(), {}, []);

    const setArchived = useSnackAction(async (input: { user: AdminUser; archived: boolean }) => {
        await api.admin.upsertUser({ ...input.user, archived: input.archived });
        list.reload();
    }, { success: "Saved" });

    const viewAs = useSnackAction(async (input: { usr: number }) => {
        await api.admin.masquerade(input);

        // full page load so every provider re-initializes against the new session
        window.location.href = "/";
    }, { success: "Viewing as user" });

    const rows = list.asList.filter(user => {
        switch (filter) {
            case "active": return !user.archived;
            case "unconfirmed": return !user.confirmedAt || user.archivedUnconfirmed;
            case "archived": return user.archived;
            default: return true;
        }
    });

    return (
        <>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <ToggleButtonGroup size="small" exclusive value={filter}
                                   onChange={(_, value: Filter | null) => value && setFilter(value)}>
                    <ToggleButton value="active">Active</ToggleButton>
                    <ToggleButton value="unconfirmed">Unconfirmed</ToggleButton>
                    <ToggleButton value="archived">Archived</ToggleButton>
                    <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>
                <div style={{ flex: 1 }} />
                <Button variant="contained" size="small" onClick={() => setEditing({})}>Add user</Button>
            </div>

            <div style={{ height: 16 }} />
            {list.LoadingOrErrorElement}

            {phone ? (
                <Paper>
                    <List disablePadding>
                        {rows.map(user => (
                            <ListItem key={user.id} divider secondaryAction={!user.isAdmin && (
                                <RowMenu items={[
                                    { label: "Edit", onClick: () => setEditing(user) },
                                    ...(!user.archived && user.confirmedAt ? [{
                                        label: "View as",
                                        disabled: viewAs.loading,
                                        onClick: () => viewAs.callback({ usr: user.id }),
                                    }] : []),
                                    {
                                        label: user.archived ? "Restore" : "Archive",
                                        danger: !user.archived,
                                        disabled: setArchived.loading,
                                        onClick: () => setArchived.callback({ user: user, archived: !user.archived }),
                                    },
                                ]} />
                            )}>
                                <ListItemText
                                    primary={
                                        <Box component="span" sx={{ overflowWrap: "anywhere" }}>
                                            {user.email}
                                            {!user.confirmedAt && <Chip size="small" label="Unconfirmed" sx={{ ml: 1 }} />}
                                            {user.archived && <Chip size="small" label="Archived" sx={{ ml: 1 }} />}
                                        </Box>
                                    }
                                    secondary={[
                                        `${user.firstName} ${user.lastName}`.trim(),
                                        user.teamCount + (user.teamCount === 1 ? " team" : " teams"),
                                        user.lastActiveAt ? moment(user.lastActiveAt).fromNow() : "",
                                    ].filter(v => !!v).join(" · ")}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            ) : (
                <Paper>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Email</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell align="right">Teams</TableCell>
                                <TableCell>Last active</TableCell>
                                <TableCell padding="checkbox" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(user => (
                                <TableRow key={user.id}>
                                    <TableCell>
                                        {user.email}
                                        {!user.confirmedAt && <Chip size="small" label="Unconfirmed" sx={{ ml: 1 }} />}
                                        {user.archived && <Chip size="small" label="Archived" sx={{ ml: 1 }} />}
                                    </TableCell>
                                    <TableCell>{user.firstName} {user.lastName}</TableCell>
                                    <TableCell align="right">{user.teamCount}</TableCell>
                                    <TableCell style={{ whiteSpace: "nowrap" }}>
                                        {user.lastActiveAt ? moment(user.lastActiveAt).fromNow() : ""}
                                    </TableCell>
                                    <TableCell padding="checkbox" style={{ whiteSpace: "nowrap" }}>
                                        {!user.isAdmin && (
                                            <>
                                                <Button size="small" onClick={() => setEditing(user)}>Edit</Button>
                                                {!user.archived && user.confirmedAt && (
                                                    <Button size="small" disabled={viewAs.loading}
                                                            onClick={() => viewAs.callback({ usr: user.id })}>
                                                        View as
                                                    </Button>
                                            )}
                                            <Button size="small" color={user.archived ? "primary" : "error"}
                                                    disabled={setArchived.loading}
                                                    onClick={() => setArchived.callback({ user: user, archived: !user.archived })}>
                                                {user.archived ? "Restore" : "Archive"}
                                            </Button>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
            )}

            {editing && (
                <UserEditor
                    value={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        list.reload();
                    }}
                />
            )}
        </>
    );
}

// UserEditor adds a user or edits one. The password field sets a new password; left
// blank on an edit, the password stays as it is.
function UserEditor(props: { value: Partial<AdminUser>; onClose: () => void; onSaved: () => void }) {
    const [email, setEmail] = useState(props.value.email || "");
    const [firstName, setFirstName] = useState(props.value.firstName || "");
    const [lastName, setLastName] = useState(props.value.lastName || "");
    const [password, setPassword] = useState("");

    const isNew = !props.value.id;

    const save = useAsyncAction(async () => {
        await api.admin.upsertUser({
            id: props.value.id,
            email: email,
            firstName: firstName,
            lastName: lastName,
            archived: props.value.archived || false,
            password: password || undefined,
        });

        props.onSaved();
    }, [email, firstName, lastName, password]);

    return (
        <Dialog open onClose={props.onClose} maxWidth="xs" fullWidth>
            <form onSubmit={e => {
                e.preventDefault();
                save.callback(null);
            }}>
                <DialogTitle>{isNew ? "Add user" : "Edit user"}</DialogTitle>
                <DialogContent style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 8 }}>
                    <TextField label="Email" value={email} onChange={e => setEmail(e.target.value)} />
                    <TextField label="First name" value={firstName} onChange={e => setFirstName(e.target.value)} />
                    <TextField label="Last name" value={lastName} onChange={e => setLastName(e.target.value)} />
                    <TextField label={isNew ? "Password" : "New password"} type="password"
                               helperText={isNew ? undefined : "Leave blank to keep the password"}
                               value={password} onChange={e => setPassword(e.target.value)} />
                    {save.LoadingElement}
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={save.loading}>Save</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
